/**
 * O catálogo de planos ligado ao servidor (#766): lê o catálogo, abre o
 * formulário de criar e de editar, e manda o pedido.
 *
 * A tela (`TelaDePlanos`) nasceu antes do back e recebe tudo pelas props; esta
 * é quem a monta. Salvou, o formulário fecha e o catálogo é lido de novo, com
 * a contagem de organizações que só o servidor sabe.
 */

import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CriarPlano, EditarPlano } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useApi, usePlanos } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { planoDoCatalogo } from '../model/plano-do-catalogo';
import { DialogoDoPlano } from './dialogo-do-plano';
import { TelaDePlanos, type TelaDePlanosProps } from './tela-de-planos';

export type PaginaDePlanosProps = {
  aoTrocarDeAba: TelaDePlanosProps['aoTrocarDeAba'];
};

type Aberto = { readonly tipo: 'novo' } | { readonly tipo: 'editar'; readonly id: string } | null;

type Pedido =
  | { readonly tipo: 'novo'; readonly corpo: CriarPlano }
  | { readonly tipo: 'editar'; readonly id: string; readonly corpo: EditarPlano };

export function PaginaDePlanos({ aoTrocarDeAba }: PaginaDePlanosProps): ReactNode {
  const consulta = usePlanos();
  const api = useApi();
  const invalidar = useInvalidar();
  const [aberto, definirAberto] = useState<Aberto>(null);
  const planos = consulta.data?.itens.map(planoDoCatalogo);

  const salvar = useMutation({
    mutationFn: async (pedido: Pedido) => {
      if (!api) throw new Error('Sem sessão.');
      return pedido.tipo === 'novo'
        ? api.criarPlano({ corpo: pedido.corpo })
        : api.editarPlano({ caminho: { id: pedido.id }, corpo: pedido.corpo });
    },
    onSuccess: () => {
      definirAberto(null);
      invalidar([chaves.planos()]);
    },
  });

  function abrir(proximo: Aberto): void {
    salvar.reset();
    definirAberto(proximo);
  }

  const emEdicao =
    aberto?.tipo === 'editar' ? (planos?.find((plano) => plano.id === aberto.id) ?? null) : null;

  return (
    <>
      <TelaDePlanos
        planos={planos}
        erro={consulta.error}
        aoRepetir={() => void consulta.refetch()}
        aoCriar={() => abrir({ tipo: 'novo' })}
        aoEditar={(id) => abrir({ tipo: 'editar', id })}
        aoTrocarDeAba={aoTrocarDeAba}
      />
      {aberto && (aberto.tipo === 'novo' || emEdicao) && (
        <DialogoDoPlano
          key={aberto.tipo === 'novo' ? 'novo' : aberto.id}
          plano={emEdicao}
          enviando={salvar.isPending}
          falha={
            salvar.error
              ? recadoDeErro(salvar.error, { generica: 'Não foi possível salvar o plano.' })
              : null
          }
          aoCriar={(corpo) => salvar.mutate({ tipo: 'novo', corpo })}
          aoEditar={(corpo) => {
            if (aberto.tipo === 'editar') salvar.mutate({ tipo: 'editar', id: aberto.id, corpo });
          }}
          aoFechar={() => definirAberto(null)}
        />
      )}
    </>
  );
}
